import React, { useState, useMemo } from 'react';
import { motion } from 'framer-motion';
import { Slider } from './ui/slider';
import { TrendingUp, Sparkles, Info } from 'lucide-react';

const formatCurrency = (value) => {
  return new Intl.NumberFormat('de-DE', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
};

// Financial constants matching backend
const ASSUMPTIONS = {
  GROSS_MARKET_RETURN: 0.07,
  ETF_ANNUAL_COSTS: 0.003,
  ETF_ANNUAL_TAX_DRAG: 0.008,
  WITHDRAWAL_RATE: 0.035,
};

// Simple wealth projection matching backend formula
const projectWealth = (initialCapital, monthlyContribution, years) => {
  const netReturn = ASSUMPTIONS.GROSS_MARKET_RETURN - ASSUMPTIONS.ETF_ANNUAL_COSTS - ASSUMPTIONS.ETF_ANNUAL_TAX_DRAG;
  const monthlyReturn = Math.pow(1 + netReturn, 1/12) - 1;
  let wealth = initialCapital;
  const totalMonths = years * 12;
  
  for (let month = 0; month < totalMonths; month++) {
    wealth = wealth * (1 + monthlyReturn) + monthlyContribution;
  }
  
  return Math.round(wealth);
};

export const WhatIfSlider = ({ 
  // Current values from analysis
  currentSavingsRate,
  currentCapital,
  currentPension,
  yearsToRetirement,
  initialAssets = 0,
  gesetzlicheRente = 0,
  privateRente = 0,
}) => {
  // "What if" slider state
  const [whatIfSavings, setWhatIfSavings] = useState(currentSavingsRate);
  
  // Calculate what-if scenario
  const whatIfResult = useMemo(() => {
    const projectedCapital = projectWealth(initialAssets, whatIfSavings, yearsToRetirement);
    const capitalPension = Math.round((projectedCapital * ASSUMPTIONS.WITHDRAWAL_RATE) / 12);
    const totalPension = capitalPension + gesetzlicheRente + privateRente;
    
    return {
      capital: projectedCapital,
      capitalPension,
      totalPension,
    };
  }, [whatIfSavings, yearsToRetirement, initialAssets, gesetzlicheRente, privateRente]);
  
  // Calculate differences
  const capitalDiff = whatIfResult.capital - currentCapital;
  const pensionDiff = whatIfResult.totalPension - currentPension;
  const savingsDiff = whatIfSavings - currentSavingsRate;
  const isIncrease = savingsDiff > 0;
  const hasChange = Math.abs(savingsDiff) >= 25;
  
  return (
    <div className="bg-gradient-to-br from-cobalt/5 to-navy/5 rounded-2xl border border-cobalt/20 p-6 mb-8" data-testid="what-if-slider">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 bg-cobalt/10 rounded-xl flex items-center justify-center">
          <Sparkles className="w-5 h-5 text-cobalt" />
        </div>
        <div>
          <h3 className="font-outfit text-lg font-bold text-navy">Was wäre wenn?</h3>
          <p className="text-sm text-slate">Sehen Sie, wie sich Änderungen auswirken</p>
        </div>
      </div>
      
      {/* Slider */}
      <div className="bg-white rounded-xl p-5 mb-4">
        <div className="flex items-center justify-between mb-3">
          <span className="text-sm font-medium text-charcoal">Monatliche Sparrate</span>
          <div className="flex items-center gap-2">
            <span className="font-mono text-xl font-bold text-navy">{formatCurrency(whatIfSavings)}</span>
            {hasChange && (
              <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                isIncrease ? 'bg-emerald/10 text-emerald' : 'bg-orange/10 text-orange'
              }`}>
                {isIncrease ? '+' : ''}{formatCurrency(savingsDiff)}
              </span>
            )}
          </div>
        </div>
        <Slider
          value={[whatIfSavings]}
          onValueChange={(val) => setWhatIfSavings(val[0])}
          min={0}
          max={Math.max(3000, currentSavingsRate * 2)}
          step={50}
          className="my-2"
          data-testid="what-if-slider-input"
        />
        <div className="flex justify-between text-xs text-slate">
          <span>€0</span>
          <span className="text-cobalt font-medium">Aktuell: {formatCurrency(currentSavingsRate)}</span>
          <span>{formatCurrency(Math.max(3000, currentSavingsRate * 2))}</span>
        </div>
      </div>
      
      {/* Results comparison */}
      {hasChange && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="space-y-3"
        >
          {/* Capital comparison */}
          <div className="bg-white rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-xs text-slate uppercase tracking-wider mb-1">Kapital bei Rentenbeginn</div>
                <div className="flex items-center gap-2">
                  <span className="font-mono text-lg text-slate line-through">{formatCurrency(currentCapital)}</span>
                  <TrendingUp className={`w-4 h-4 ${isIncrease ? 'text-emerald' : 'text-orange rotate-180'}`} />
                  <span className="font-mono text-xl font-bold text-navy">{formatCurrency(whatIfResult.capital)}</span>
                </div>
              </div>
              <div className={`text-right px-3 py-1 rounded-lg ${
                isIncrease ? 'bg-emerald/10' : 'bg-orange/10'
              }`}>
                <div className={`font-mono text-lg font-bold ${isIncrease ? 'text-emerald' : 'text-orange'}`}>
                  {isIncrease ? '+' : ''}{formatCurrency(capitalDiff)}
                </div>
              </div>
            </div>
          </div>
          
          {/* Pension comparison */}
          <div className="bg-white rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-xs text-slate uppercase tracking-wider mb-1">Monatliche Rente</div>
                <div className="flex items-center gap-2">
                  <span className="font-mono text-lg text-slate line-through">{formatCurrency(currentPension)}</span>
                  <TrendingUp className={`w-4 h-4 ${isIncrease ? 'text-emerald' : 'text-orange rotate-180'}`} />
                  <span className="font-mono text-xl font-bold text-navy">{formatCurrency(whatIfResult.totalPension)}</span>
                </div>
              </div>
              <div className={`text-right px-3 py-1 rounded-lg ${
                isIncrease ? 'bg-emerald/10' : 'bg-orange/10'
              }`}>
                <div className={`font-mono text-lg font-bold ${isIncrease ? 'text-emerald' : 'text-orange'}`}>
                  {isIncrease ? '+' : ''}{formatCurrency(pensionDiff)}/M
                </div>
              </div>
            </div>
          </div>
          
          {/* Insight message */}
          <div className={`p-4 rounded-lg ${isIncrease ? 'bg-emerald/5 border border-emerald/20' : 'bg-orange/5 border border-orange/20'}`}>
            {isIncrease ? (
              <p className="text-sm text-charcoal">
                <span className="font-medium text-emerald">+{formatCurrency(savingsDiff)}/Monat</span> mehr sparen bedeutet 
                <span className="font-medium text-emerald"> +{formatCurrency(pensionDiff)}/Monat</span> mehr Rente – 
                das sind <span className="font-medium text-emerald">{formatCurrency(pensionDiff * 12 * 30)}</span> über 30 Jahre Ruhestand!
              </p>
            ) : (
              <p className="text-sm text-charcoal">
                <span className="font-medium text-orange">{formatCurrency(savingsDiff)}/Monat</span> weniger sparen reduziert 
                Ihre Rente um <span className="font-medium text-orange">{formatCurrency(Math.abs(pensionDiff))}/Monat</span>.
              </p>
            )}
          </div>
        </motion.div>
      )}
      
      {/* Reset hint */}
      {hasChange && (
        <div className="mt-4 flex items-center justify-center gap-2 text-xs text-slate">
          <Info className="w-3 h-3" />
          <span>Bewegen Sie den Regler auf {formatCurrency(currentSavingsRate)} um zum aktuellen Wert zurückzukehren</span>
        </div>
      )}
      
      {/* No change state */}
      {!hasChange && (
        <div className="text-center py-4 text-sm text-slate">
          Bewegen Sie den Regler um zu sehen, wie sich Änderungen auf Ihre Rente auswirken
        </div>
      )}
    </div>
  );
};

export default WhatIfSlider;
